/**
 * Times are always shown in the stacker's timezone (the site's `timezone`, Asia/Ulaanbaatar),
 * never the browser's: the same instant reads the same on every PC.
 */

type Style = "datetime" | "time" | "date";

const cache = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = cache.get(tz);
  if (f === undefined) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    cache.set(tz, f);
  }
  return f;
}

/** `2026-10-01 07:00` (or the time / the date only) of `t` in `tz`. */
export function formatLocal(t: string | number | Date, tz: string, style: Style = "datetime"): string {
  const d = t instanceof Date ? t : new Date(t);
  const parts = Object.fromEntries(formatter(tz).formatToParts(d).map((p) => [p.type, p.value]));
  const date = `${parts["year"]}-${parts["month"]}-${parts["day"]}`;
  const time = `${parts["hour"]}:${parts["minute"]}`;
  return style === "date" ? date : style === "time" ? time : `${date} ${time}`;
}

/** `45 min`, `2 h 05 min`, `3 d 4 h`. */
export function formatAge(seconds: number): string {
  const min = Math.floor(seconds / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h ${String(min % 60).padStart(2, "0")} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

/** The UTC offset of `tz` at instant `t` (ms; Ulaanbaatar +8 h). */
export function tzOffsetMs(t: number, tz: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(new Date(t))
      .map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(+parts["year"]!, +parts["month"]! - 1, +parts["day"]!, +parts["hour"]!, +parts["minute"]!, +parts["second"]!);
  return asUtc - Math.floor(t / 1000) * 1000;
}

/** A `datetime-local` input value (`2026-10-01T07:00`, the site's wall clock) → UTC ISO. */
export function localToUtcIso(value: string, tz: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) throw new Error(`not a local date-time: ${value}`);
  const wall = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  let t = wall - tzOffsetMs(wall, tz);
  t = wall - tzOffsetMs(t, tz); // once more, across an offset change
  return new Date(t).toISOString();
}

/** UTC → the `datetime-local` value of the site's wall clock. */
export function toLocalInput(t: string | number | Date, tz: string): string {
  return formatLocal(t, tz).replace(" ", "T");
}
